// ============================================================
// Ficha médica · la única vía auditada, y el aviso que no lee
// ============================================================
// Paso 2 del punto 5 de la §71 del runbook.
//
// Tres operaciones, y la diferencia entre ellas es el punto de todo esto:
//
//   · `leerFichaMedica(userId)` → el CONTENIDO, por la RPC `get_medical_info`
//     (mig 281), que deja fila en `audit_log`. Se llama **al abrir la ficha**,
//     nunca al pintar una lista.
//   · `leerFlagsFichaMedica(ids)` → solo QUIÉN TIENE ficha, por la columna
//     `tiene_ficha_medica` de la vista (mig 282). No lee el dato y no deja
//     fila, porque no hay nada que auditar: saber que un niño tiene ficha no
//     es leerla.
//   · `borrarFichaMedica(userId)` → BORRARLA a propósito, por la RPC
//     `delete_medical_info` (mig 283). Vaciarla con un UPDATE ya no se puede:
//     el trigger de la 283 devuelve 42501, para que un build viejo no la borre
//     sin querer.
//
// ⚠️ NO se llama a `leerFichaMedica` en bucle para pintar una lista. Sería
// volver a lo que se quitó: una fila de auditoría por cada vez que alguien
// abre una pantalla, y entonces el log dice «abrió una lista» y no «leyó una
// ficha», que es justo lo que hace inútil la traza.
import { supabase } from './supabase';

export type FichaMedica =
  | { estado: 'ok'; ficha: string | null }
  | { estado: 'error'; mensaje: string };

/**
 * El contenido de una ficha médica. Deja fila en `audit_log`.
 * Devuelve `ficha: null` cuando no te toca verla o cuando no hay nada escrito
 * —el mismo contrato que tenía la vista—; la auditoría sí distingue los dos
 * casos, el cliente no necesita hacerlo.
 */
export async function leerFichaMedica(userId: string): Promise<FichaMedica> {
  const { data, error } = await supabase.rpc('get_medical_info', { p_user_id: userId });
  // El error NO se descarta. Una lectura fallida que se pinta como «sin
  // información médica» es peor que un error: el entrenador se queda tranquilo
  // creyendo que el niño no tiene nada.
  if (error) return { estado: 'error', mensaje: error.message };
  return { estado: 'ok', ficha: (data as string | null) ?? null };
}

/**
 * Quiénes de esos alumnos tienen ficha. Para el aviso de una lista, sin leer
 * el dato. Devuelve el conjunto de ids con ficha.
 */
export async function leerFlagsFichaMedica(userIds: string[]): Promise<{
  conFicha: Set<string>;
  error: string | null;
}> {
  if (userIds.length === 0) return { conFicha: new Set(), error: null };
  const { data, error } = await supabase
    .from('profiles_with_medical_info')
    .select('id, tiene_ficha_medica')
    .in('id', userIds);
  if (error) {
    // ⚠️ EL HUECO DE DESPLIEGUE, a propósito. El panel sale por Vercel en
    // cuanto el PR entra en `main`; si la migración 282 aún no está aplicada,
    // la columna no existe y PostgREST responde 400. Aquí se degrada a «no sé
    // quién tiene ficha» —la lista se pinta entera, sin el aviso— en vez de
    // dejar la pantalla sin alumnos. Se devuelve el error para que quien llama
    // pueda decirlo si quiere, pero la pantalla no se cae.
    return { conFicha: new Set(), error: error.message };
  }
  const conFicha = new Set<string>();
  for (const r of (data ?? []) as { id: string; tiene_ficha_medica: boolean | null }[]) {
    if (r.tiene_ficha_medica) conFicha.add(r.id);
  }
  return { conFicha, error: null };
}

export type BorradoFicha = { estado: 'ok' } | { estado: 'error'; mensaje: string };

/**
 * Borrar la ficha médica A PROPÓSITO. Ejerce el derecho al olvido y deja fila
 * `medical_info_deleted` en `audit_log` con quién, con qué derecho y cuánto
 * había.
 *
 * ⚠️ NO se vacía con un UPDATE. La mig 283 puso un trigger que rechaza pasar
 * `medical_info` de contenido a NULL o a cadena vacía desde un rol de cliente,
 * y devuelve 42501. No es un obstáculo a esquivar: es lo que impide que un
 * build antiguo —que recibe la ficha `undefined` y arranca el formulario
 * vacío— la borre sin que nadie se entere. Esta función es la puerta buena.
 */
export async function borrarFichaMedica(userId: string): Promise<BorradoFicha> {
  const { error } = await supabase.rpc('delete_medical_info', { p_user_id: userId });
  if (error) return { estado: 'error', mensaje: error.message };
  return { estado: 'ok' };
}
